import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    IconButton,
    Alert,
    Stack,
    Paper,
    CircularProgress,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useGetApiQuery } from '../../../services/goroskop';
import { getZodiacSignFromDate, getZodiacTitle, normalizeZodiacKey } from '../../../utils/zodiac';
import { getEasternSignByYear, getEasternTitle, normalizeEasternKey } from '../../../utils/easternZodiac';

import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ruRU } from '@mui/x-date-pickers/locales';
import '../../../datepicker.css';

dayjs.locale('ru');
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.tz.setDefault('Europe/Moscow');

const DEFAULT_CONFIG = {
    intro: 'Вам успешно подобраны люди, удовлетворяющие вашим условиям поиска по возрасту.',
    incompatible: 'Ваш партнер с вами несовместим.',
    astrologer: 'За подробностями обратитесь к астрологу Вячеславу Горелову:',
    check_title: 'Экспресс-проверка совместимости',
    check_subtitle: 'Вы можете прямо сейчас проверить совместимость по дате рождения партнера:'
};

interface MatchBannerDialogProps {
    open: boolean;
    onClose: () => void;
    totalCount: number;
    mainCount?: number;
    vkCount?: number;
    isLoading?: boolean;
    userAccount?: any;
}

export const MatchBannerDialog: React.FC<MatchBannerDialogProps> = ({
    open,
    onClose,
    totalCount,
    mainCount = 0,
    vkCount = 0,
    isLoading = false,
    userAccount
}) => {
    const { data: modalConfigData } = useGetApiQuery('welcome_modal.php');

    const introText = (modalConfigData as any)?.[0]?.intro || DEFAULT_CONFIG.intro;
    const incompatibleText = (modalConfigData as any)?.[0]?.incompatible || DEFAULT_CONFIG.incompatible;
    const astrologerText = (modalConfigData as any)?.[0]?.astrologer || DEFAULT_CONFIG.astrologer;
    const checkTitleText = (modalConfigData as any)?.[0]?.check_title || DEFAULT_CONFIG.check_title;
    const checkSubtitleText = (modalConfigData as any)?.[0]?.check_subtitle || DEFAULT_CONFIG.check_subtitle;

    const [partnerDate, setPartnerDate] = useState<Dayjs | null>(null);
    const [validationError, setValidationError] = useState<string>('');
    const [isChecking, setIsChecking] = useState<boolean>(false);
    const [checkResult, setCheckResult] = useState<boolean>(false);
    const [matchedCustomText, setMatchedCustomText] = useState<string>('');
    const [matchedPairLabel, setMatchedPairLabel] = useState<string>('');

    const handleCheckCompatibility = (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError('');
        setCheckResult(false);
        setMatchedCustomText('');
        setMatchedPairLabel('');

        if (!partnerDate || !partnerDate.isValid()) {
            setValidationError('Пожалуйста, укажите дату рождения партнера');
            return;
        }

        const today = dayjs();
        if (partnerDate.isAfter(today)) {
            setValidationError('Дата рождения не может быть в будущем');
            return;
        }

        // Вычисляем полный возраст
        const age = today.diff(partnerDate, 'year');

        if (age < 18) {
            setValidationError('Возраст партнера должен быть не менее 18 лет');
            return;
        }

        // Извлекаем сохраненные матрицы
        const zodiacPairs = (modalConfigData as any)?.[0]?.zodiac_pairs || {};
        let easternPairs = (modalConfigData as any)?.[0]?.eastern_pairs;
        if (!easternPairs || typeof easternPairs !== 'object') {
            try {
                const c = localStorage.getItem('astro_eastern_pairs');
                easternPairs = c ? JSON.parse(c) : {};
            } catch (e) { easternPairs = {}; }
        }
        let numberPairs = (modalConfigData as any)?.[0]?.number_pairs;
        if (!numberPairs || typeof numberPairs !== 'object') {
            try {
                const c = localStorage.getItem('astro_number_pairs');
                numberPairs = c ? JSON.parse(c) : {};
            } catch (e) { numberPairs = {}; }
        }

        // Данные партнера
        const pDay = partnerDate.date();
        const pMonth = partnerDate.month() + 1;
        const pYear = partnerDate.year();
        const pZodiac = getZodiacSignFromDate(pDay, pMonth);
        const pEastern = getEasternSignByYear(pYear)?.key;

        // Данные пользователя
        const userBdayStr: string = userAccount?.birthday || userAccount?.birthd || '';
        let uDay: number | null = null;
        let uMonth: number | null = null;
        let uYear: number | null = null;
        if (userBdayStr) {
            const m = userBdayStr.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
            if (m) {
                uDay = parseInt(m[1], 10);
                uMonth = parseInt(m[2], 10);
                uYear = parseInt(m[3], 10);
            }
        }
        const uZodiac = userAccount?.zodiak
            ? normalizeZodiacKey(userAccount.zodiak)
            : (uDay && uMonth ? getZodiacSignFromDate(uDay, uMonth) : '');
        const uEastern = userAccount?.animal
            ? normalizeEasternKey(userAccount.animal)
            : (uYear ? getEasternSignByYear(uYear)?.key : '');

        let resolvedCustomText = '';
        let resolvedLabel = '';

        // 1. Поиск по числам дня рождения (1..31)
        if (uDay && pDay && numberPairs) {
            const k1 = `${uDay}_${pDay}`;
            const k2 = `${pDay}_${uDay}`;
            if (numberPairs[k1]) {
                resolvedCustomText = numberPairs[k1];
                resolvedLabel = `Числа рождения: ${uDay}-е и ${pDay}-е`;
            } else if (numberPairs[k2]) {
                resolvedCustomText = numberPairs[k2];
                resolvedLabel = `Числа рождения: ${pDay}-е и ${uDay}-е`;
            }
        }

        // 2. Поиск по китайскому календарю (животные)
        if (!resolvedCustomText && uEastern && pEastern && easternPairs) {
            const k1 = `${uEastern}_${pEastern}`;
            const k2 = `${pEastern}_${uEastern}`;
            if (easternPairs[k1]) {
                resolvedCustomText = easternPairs[k1];
                resolvedLabel = `Китайский календарь: ${getEasternTitle(uEastern)} + ${getEasternTitle(pEastern)}`;
            } else if (easternPairs[k2]) {
                resolvedCustomText = easternPairs[k2];
                resolvedLabel = `Китайский календарь: ${getEasternTitle(pEastern)} + ${getEasternTitle(uEastern)}`;
            }
        }

        // 3. Поиск по западному знаку зодиака
        if (!resolvedCustomText && uZodiac && pZodiac && zodiacPairs) {
            const k1 = `${uZodiac}_${pZodiac}`;
            const k2 = `${pZodiac}_${uZodiac}`;
            if (zodiacPairs[k1]) {
                resolvedCustomText = zodiacPairs[k1];
                resolvedLabel = `Знаки зодиака: ${getZodiacTitle(uZodiac)} + ${getZodiacTitle(pZodiac)}`;
            } else if (zodiacPairs[k2]) {
                resolvedCustomText = zodiacPairs[k2];
                resolvedLabel = `Знаки зодиака: ${getZodiacTitle(pZodiac)} + ${getZodiacTitle(uZodiac)}`;
            }
        }

        setMatchedCustomText(resolvedCustomText);
        setMatchedPairLabel(resolvedLabel);

        // Запускаем 1-секундную анимацию лоадера перед отображением вердикта
        setIsChecking(true);
        setTimeout(() => {
            setIsChecking(false);
            setCheckResult(true);
        }, 1000);
    };

    const handleDateChange = (val: Dayjs | null) => {
        setPartnerDate(val);
        if (validationError) {
            setValidationError('');
        }
        if (checkResult) {
            setCheckResult(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    m: { xs: 1.5, sm: 3 },
                    width: { xs: 'calc(100% - 24px)', sm: 'auto' },
                    maxWidth: { xs: '440px', sm: '560px' },
                    borderRadius: { xs: 2.5, sm: 3 },
                    p: { xs: 0.5, sm: 1.5 },
                    background: 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)',
                    boxShadow: '0 12px 36px rgba(25, 118, 210, 0.15)',
                }
            }}
        >
            <Box sx={{display: 'flex', justifyContent: 'flex-end', position: 'relative'}}>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        right: { xs: 2, sm: 0 },
                        top: { xs: 2, sm: 0 },
                        color: (theme) => theme.palette.grey[500],
                        p: { xs: 0.5, sm: 1 }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogTitle sx={{textAlign: 'center', pt: { xs: 1.2, sm: 1 }, pb: { xs: 0.5, sm: 1 }, px: { xs: 1.5, sm: 2 }}}>
                <Typography variant="h6" component="div" sx={{fontWeight: 700, color: '#1a237e', lineHeight: 1.25, fontSize: { xs: '1.05rem', sm: '1.25rem' }}}>
                    Здравствуйте!
                </Typography>
                <Typography variant="body1" sx={{color: '#455a64', mt: { xs: 0.5, sm: 1 }, px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.82rem', sm: '0.95rem' }, lineHeight: { xs: 1.35, sm: 1.45 }}}>
                    {introText}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{pt: { xs: 0.5, sm: 1 }, px: { xs: 1.5, sm: 3 }, pb: { xs: 0.5, sm: 1 }}}>
                {/* Плашка с количеством подобраных людей */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 1, sm: 1.8 },
                        mb: { xs: 1.5, sm: 2.5 },
                        backgroundColor: '#eef5fc',
                        borderRadius: { xs: 2, sm: 2.5 },
                        border: '1px solid #c9e0fa'
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: 0.8, sm: 1.5 }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <PeopleAltIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 24 } }} />
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#37474f', fontSize: { xs: '0.84rem', sm: '0.95rem' } }}>
                                Их количество составило:
                            </Typography>
                        </Box>

                        {isLoading ? (
                            <CircularProgress size={20} sx={{ mx: 0.5 }} />
                        ) : (
                            <Typography
                                variant="h4"
                                component="span"
                                sx={{
                                    fontWeight: 800,
                                    color: '#1565c0',
                                    fontSize: { xs: '1.25rem', sm: '1.8rem' },
                                    lineHeight: 1,
                                    px: { xs: 0.8, sm: 1.2 },
                                    py: { xs: 0.2, sm: 0.3 },
                                    bgcolor: '#ffffff',
                                    borderRadius: 1.5,
                                    border: '1px solid #bbdefb',
                                    boxShadow: '0 1px 4px rgba(25,118,210,0.1)'
                                }}
                            >
                                {totalCount}
                            </Typography>
                        )}

                        <Typography variant="body2" sx={{ color: '#546e7a', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                            (Основная база: <b>{mainCount}</b> • Подписчики ВК: <b>{vkCount}</b>)
                        </Typography>
                    </Box>
                </Paper>

                {/* Блок экспресс-проверки / результатов проверки */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 1.5, sm: 2.2 },
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 2, sm: 2.5 },
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
                    }}
                >
                    {!checkResult ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: { xs: 0.5, sm: 1 } }}>
                                <FavoriteIcon sx={{ color: '#e91e63', fontSize: { xs: 18, sm: 22 } }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#263238', fontSize: { xs: '0.88rem', sm: '1rem' } }}>
                                    {checkTitleText}
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#546e7a', mb: { xs: 1.2, sm: 2 }, fontSize: { xs: '0.76rem', sm: '0.875rem' }, lineHeight: { xs: 1.35, sm: 1.43 } }}>
                                {checkSubtitleText}
                            </Typography>

                            <LocalizationProvider
                                adapterLocale="ru"
                                dateAdapter={AdapterDayjs}
                                localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
                            >
                                <Box component="form" onSubmit={handleCheckCompatibility}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 1.5 }} alignItems="stretch">
                                        <DatePicker
                                            label="Дата рождения партнера"
                                            format="DD.MM.YYYY"
                                            value={partnerDate}
                                            onChange={handleDateChange}
                                            minDate={dayjs('1900-01-01')}
                                            maxDate={dayjs().subtract(18, 'year')}
                                            sx={{
                                                flex: 1,
                                                width: { xs: '100%', sm: 'auto' },
                                            }}
                                            slotProps={{
                                                inputAdornment: {
                                                    position: 'end'
                                                },
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                    error: !!validationError,
                                                    helperText: validationError,
                                                    placeholder: 'ДД.ММ.ГГГГ',
                                                    sx: {
                                                        '& .MuiInputBase-input': {
                                                            fontSize: { xs: '0.82rem', sm: '0.92rem' },
                                                            py: { xs: 0.7, sm: 1 }
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            fontSize: { xs: '0.8rem', sm: '0.88rem' }
                                                        }
                                                    }
                                                },
                                                popper: {
                                                    sx: { zIndex: 1500 }
                                                },
                                                dialog: {
                                                    sx: { zIndex: 1500 }
                                                },
                                                day: {
                                                    sx: {
                                                        '&:hover': {
                                                            backgroundColor: '#02D2D2',
                                                            transition: 'background-color 0.3s ease',
                                                            color: '#fff'
                                                        },
                                                        '&.MuiButtonBase-root.MuiPickersDay-root.Mui-selected': {
                                                            backgroundColor: '#05B5CD'
                                                        },
                                                        '&.Mui-selected': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                            '&:hover': {
                                                                color: '#fff',
                                                                backgroundColor: '#05B5CD',
                                                                transition: 'background-color 0.3s ease',
                                                            },
                                                            '&:focus': {
                                                                color: '#fff',
                                                                backgroundColor: '#05B5CD',
                                                                transition: 'background-color 0.3s ease',
                                                            },
                                                        },
                                                        '&.Mui-selected:focus': {
                                                            backgroundColor: '#05B5CD',
                                                            color: '#fff'
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: '#05B5CD',
                                                            color: '#fff'
                                                        },
                                                        '.MuiPickersMonth-monthButton.Mui-selected': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                        },
                                                        '.MuiPickersYear-yearButton.Mui-selected': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton:focus': {
                                                            backgroundColor: '#02D2D2',
                                                            color: '#fff'
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton:hover': {
                                                            backgroundColor: '#02D2D2',
                                                            color: '#fff'
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:focus': {
                                                            backgroundColor: '#05B5CD'
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:hover': {
                                                            backgroundColor: '#05B5CD'
                                                        },
                                                    }
                                                },
                                                desktopPaper: {
                                                    sx: {
                                                        '.MuiPickersMonth-monthButton.Mui-selected': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                        },
                                                        '.MuiPickersYear-yearButton.Mui-selected': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton:focus': {
                                                            backgroundColor: '#02D2D2',
                                                            color: '#fff'
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton:hover': {
                                                            backgroundColor: '#02D2D2',
                                                            color: '#fff'
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:focus': {
                                                            backgroundColor: '#05B5CD'
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:hover': {
                                                            backgroundColor: '#05B5CD'
                                                        },
                                                    },
                                                },
                                                mobilePaper: {
                                                    sx: {
                                                        '.MuiPickersMonth-monthButton.Mui-selected': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                        },
                                                        '.MuiPickersYear-yearButton.Mui-selected': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton:focus': {
                                                            backgroundColor: '#02D2D2'
                                                        },
                                                        '.MuiPickersYear-root > .MuiPickersYear-yearButton:hover': {
                                                            backgroundColor: '#02D2D2'
                                                        },
                                                        '.MuiPickersYear-button.Mui-selected:focus': {
                                                            backgroundColor: '#05B5CD'
                                                        },
                                                        '.MuiPickersYear-yearButton.Mui-selected:hover': {
                                                            backgroundColor: '#05B5CD'
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={isChecking}
                                            sx={{
                                                height: { xs: 36, sm: 40 },
                                                px: { xs: 2, sm: 3 },
                                                whiteSpace: 'nowrap',
                                                width: { xs: '100%', sm: 'auto' },
                                                fontWeight: 600,
                                                fontSize: { xs: '0.82rem', sm: '0.875rem' },
                                                boxShadow: 'none'
                                            }}
                                        >
                                            {isChecking ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CircularProgress size={16} color="inherit" />
                                                    <span>Проверка...</span>
                                                </Box>
                                            ) : (
                                                'Проверить'
                                            )}
                                        </Button>
                                    </Stack>
                                </Box>
                            </LocalizationProvider>

                            {/* Индикатор загрузки проверки */}
                            {isChecking && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2, py: { xs: 1.5, sm: 2.5 } }}>
                                    <CircularProgress size={20} sx={{ color: '#1976d2' }} />
                                    <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                        Проверка совместимости...
                                    </Typography>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box>
                            <Alert
                                severity={matchedCustomText ? "info" : "warning"}
                                icon={<CheckCircleOutlineIcon fontSize="inherit" sx={{ fontSize: { xs: 18, sm: 22 } }} />}
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: matchedCustomText ? '#f0f7ff' : '#fff8e1',
                                    border: matchedCustomText ? '1px solid #bbdefb' : '1px solid #ffe082',
                                    color: matchedCustomText ? '#1565c0' : '#5d4037',
                                    p: { xs: 1, sm: 1.5 },
                                    '& .MuiAlert-icon': {
                                        color: matchedCustomText ? '#1976d2' : '#f57c00'
                                    }
                                }}
                            >
                                {matchedPairLabel && (
                                    <Box sx={{ mb: 0.8 }}>
                                        <Chip
                                            size="small"
                                            label={matchedPairLabel}
                                            color="primary"
                                            sx={{ height: 22, fontSize: '0.74rem', fontWeight: 700 }}
                                        />
                                    </Box>
                                )}
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                                    {matchedCustomText || incompatibleText}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1, color: '#4e342e', fontSize: { xs: '0.78rem', sm: '0.875rem' }, lineHeight: { xs: 1.35, sm: 1.45 } }}>
                                    {astrologerText}
                                </Typography>
                                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        href="https://vk.me/svastro"
                                        target="_blank"
                                        rel="nofollow noreferrer"
                                        startIcon={
                                            <Box
                                                component="img"
                                                src="/images/social/vk.svg"
                                                alt="VK"
                                                sx={{ width: { xs: 16, sm: 18 }, height: { xs: 16, sm: 18 }, borderRadius: '4px', display: 'block' }}
                                            />
                                        }
                                        sx={{
                                            py: { xs: 0.35, sm: 0.5 },
                                            px: { xs: 1, sm: 1.5 },
                                            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                            borderColor: '#0077ff',
                                            color: '#0077ff',
                                            fontWeight: 600,
                                            '&:hover': {
                                                borderColor: '#005bbf',
                                                backgroundColor: 'rgba(0, 119, 255, 0.05)'
                                            }
                                        }}
                                    >
                                        <Box component="span" sx={{ position: 'relative', top: '1px' }}>VK</Box>
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        href="https://t.me/svat_astrolog_com"
                                        target="_blank"
                                        rel="nofollow noreferrer"
                                        startIcon={
                                            <Box
                                                component="img"
                                                src="/images/social/tg.svg"
                                                alt="Telegram"
                                                sx={{ width: { xs: 16, sm: 18 }, height: { xs: 16, sm: 18 }, borderRadius: '50%', display: 'block' }}
                                            />
                                        }
                                        sx={{
                                            py: { xs: 0.35, sm: 0.5 },
                                            px: { xs: 1, sm: 1.5 },
                                            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                            borderColor: '#29b6f6',
                                            color: '#0288d1',
                                            fontWeight: 600,
                                            '&:hover': {
                                                borderColor: '#0277bd',
                                                backgroundColor: 'rgba(41, 182, 246, 0.05)'
                                            }
                                        }}
                                    >
                                        <Box component="span" sx={{ position: 'relative', top: '1px' }}>Telegram</Box>
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        href="https://api.whatsapp.com/send?phone=79506142432"
                                        target="_blank"
                                        rel="nofollow noreferrer"
                                        startIcon={
                                            <Box
                                                component="img"
                                                src="/images/social/wa.svg"
                                                alt="WhatsApp"
                                                sx={{ width: { xs: 16, sm: 18 }, height: { xs: 16, sm: 18 }, borderRadius: '50%', display: 'block' }}
                                            />
                                        }
                                        sx={{
                                            py: { xs: 0.35, sm: 0.5 },
                                            px: { xs: 1, sm: 1.5 },
                                            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                            borderColor: '#4caf50',
                                            color: '#2e7d32',
                                            fontWeight: 600,
                                            '&:hover': {
                                                borderColor: '#1b5e20',
                                                backgroundColor: 'rgba(76, 175, 80, 0.05)'
                                            }
                                        }}
                                    >
                                        <Box component="span" sx={{ position: 'relative', top: '1px' }}>WhatsApp</Box>
                                    </Button>
                                </Stack>
                            </Alert>
                        </Box>
                    )}
                </Paper>
            </DialogContent>

            <DialogActions sx={{px: {xs: 1.5, sm: 3}, pb: {xs: 1.5, sm: 2.5}, pt: {xs: 0.5, sm: 1}}}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    fullWidth
                    size="medium"
                    sx={{
                        py: { xs: 0.9, sm: 1.3 },
                        fontWeight: 700,
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                        textTransform: 'none',
                        borderRadius: 2,
                        backgroundColor: '#1976d2',
                        boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            backgroundColor: '#1565c0',
                            boxShadow: '0 6px 18px rgba(25, 118, 210, 0.4)',
                        }
                    }}
                >
                    Перейти к просмотру анкет
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MatchBannerDialog;
